/**
 * Multiple-choice guess options for a daily mystery (deterministic per date).
 */

import { DAILY_POOL, getModelById } from './models.js';

/** Extra decoy names by category (not necessarily playable models). */
const DECOY_BANK = {
  animals: ['Dog', 'Owl', 'Wolf', 'Bear', 'Duck', 'Fish', 'Horse', 'Rabbit', 'Deer', 'Bird'],
  characters: [
    'Wizard',
    'Archer',
    'Pirate',
    'Robot',
    'Ninja',
    'Queen',
    'Soldier',
    'Chef',
    'Ghost',
    'Alien',
    'Samurai',
    'Mom',
    'Sasami',
    'Tama',
    'Tsurugi',
    'Tale',
  ],
  objects: [
    'Chair',
    'Lamp',
    'Key',
    'Crown',
    'Hammer',
    'Bottle',
    'Book',
    'Anchor',
    'Clock',
    'Shield',
    'Bow',
  ],
  space: ['Rocket', 'Planet', 'Star', 'Comet', 'Satellite', 'UFO', 'Moon'],
  tutorial: ['Heart', 'Star', 'Cube'],
};

function hashDay(dateStr) {
  let h = 2166136261;
  const s = String(dateStr || '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace(arr, rand) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function uniquePush(list, seen, name) {
  const key = String(name || '')
    .trim()
    .toLowerCase();
  if (!key || seen.has(key)) return;
  seen.add(key);
  list.push(String(name).trim());
}

/**
 * Build 8–12 display names: 1 correct + decoys (same category first).
 * Deterministic for the same date + model id.
 */
export function buildGuessOptions(model, dateStr, count = 10) {
  const target = Math.max(6, Math.min(12, count));
  const correct = model?.name || 'Mystery';
  const category = model?.category || 'objects';
  const seen = new Set();
  const sameCat = [];
  const other = [];

  uniquePush(sameCat, seen, correct);

  for (const id of DAILY_POOL.slice(0, 80)) {
    const m = getModelById(id);
    if (!m?.name) continue;
    if (m.category === category) uniquePush(sameCat, seen, m.name);
    else uniquePush(other, seen, m.name);
  }

  for (const name of DECOY_BANK[category] || []) uniquePush(sameCat, seen, name);
  for (const [cat, names] of Object.entries(DECOY_BANK)) {
    if (cat === category) continue;
    for (const name of names) uniquePush(other, seen, name);
  }

  const rand = mulberry32(hashDay(dateStr) ^ hashDay(model?.id || correct));
  shuffleInPlace(sameCat, rand);
  shuffleInPlace(other, rand);

  // Ensure correct is included, then fill from same-cat, then others
  const pool = [];
  const poolSeen = new Set();
  uniquePush(pool, poolSeen, correct);
  for (const n of sameCat) {
    if (pool.length >= target) break;
    uniquePush(pool, poolSeen, n);
  }
  for (const n of other) {
    if (pool.length >= target) break;
    uniquePush(pool, poolSeen, n);
  }

  shuffleInPlace(pool, rand);
  return {
    correct,
    options: pool,
  };
}
